import React from "react";
import { SiRoku, SiAppstore, SiGoogletv, SiGoogleplay, SiAppletv } from "react-icons/si";

/** Fire TV wordmark with the recognizable amazon smile arrow underneath. */
function FireTvMark() {
  return (
    <div className="flex flex-col items-center gap-1" aria-label="Fire TV">
      <div className="flex items-baseline gap-2 leading-none">
        <span className="text-[#FF9900] font-bold tracking-tight text-[34px] lowercase leading-none">fire</span>
        <span className="text-white font-bold tracking-tight text-[26px] lowercase leading-none">tv</span>
      </div>
      <svg width="90" height="12" viewBox="0 0 90 12" fill="none" aria-hidden="true">
        <path d="M2 3 C 25 12, 65 12, 88 3" stroke="#FF9900" strokeWidth="2.5" strokeLinecap="round" fill="none" />
        <path d="M84 2 L 88 3 L 86 7" stroke="#FF9900" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" fill="none" />
      </svg>
    </div>
  );
}

/**
 * The 5 platforms in YCN's official promo. Each icon is rendered in its
 * authentic brand color so the logos "pop" against the black canvas.
 */
const PLATFORMS = [
  {
    key: "apple-tv",
    label: "Apple TV",
    href: "#",
    // Rainbow-gradient Apple TV mark, matches Apple's colorful branding.
    render: () => (
      <>
        <svg width="0" height="0" style={{ position: "absolute" }} aria-hidden="true">
          <defs>
            <linearGradient id="atvGrad" x1="0" y1="0" x2="1" y2="1">
              <stop offset="0%" stopColor="#FF6B9D" />
              <stop offset="25%" stopColor="#F5A623" />
              <stop offset="50%" stopColor="#7ED321" />
              <stop offset="75%" stopColor="#4A90E2" />
              <stop offset="100%" stopColor="#9013FE" />
            </linearGradient>
          </defs>
        </svg>
        <SiAppletv size={68} style={{ fill: "url(#atvGrad)" }} />
      </>
    ),
  },
  {
    key: "roku",
    label: "Roku",
    href: "#",
    render: () => <SiRoku size={80} color="#6E31FF" />,
  },
  {
    key: "app-store",
    label: "App Store",
    href: "#",
    render: () => <SiAppstore size={62} color="#FFFFFF" />,
  },
  {
    key: "google-play",
    label: "Google Play",
    href: "#",
    // Real Google Play triangle in the four Google colors.
    render: () => (
      <svg width="60" height="66" viewBox="0 0 512 512" aria-hidden="true">
        <path fill="#00D5FF" d="M25 26v460l246-230z" />
        <path fill="#00F076" d="M25 26 271 256 355 172 89 20z" />
        <path fill="#FFCE00" d="M355 172l87 84L355 340l87-84z" />
        <path fill="#FF3A44" d="M25 486l246-230 84 84z" />
      </svg>
    ),
  },
  {
    key: "fire-tv",
    label: "Fire TV",
    href: "#",
    render: () => <FireTvMark />,
  },
];

export default function WhereToWatch({ testId = "where-to-watch" }) {
  return (
    <section data-testid={testId} className="mt-16 md:mt-24 px-6 md:px-12">
      <div className="max-w-[1400px] mx-auto">
        {/* Heading — lighter serif-style, wide letter-spacing */}
        <div className="text-center">
          <h2
            className="font-light uppercase tracking-[0.4em] text-3xl md:text-5xl text-white"
            style={{ fontFamily: '"Playfair Display", "Cormorant Garamond", Georgia, serif' }}
          >
            Where to Watch
          </h2>
          <div className="mx-auto mt-5 h-[2px] w-28 bg-gradient-to-r from-transparent via-white/70 to-transparent rounded-full" />
          <p className="mt-8 text-white/70 text-sm md:text-base tracking-[0.15em] uppercase">
            Enjoy <span className="ycn-gradient-text font-bold">YCN</span> on these platforms…
          </p>
        </div>

        {/* Logo row — free-floating, no card boxes */}
        <ul
          data-testid={`${testId}-list`}
          className="mt-12 md:mt-16 flex flex-wrap items-center justify-center gap-x-14 gap-y-10 md:gap-x-20 md:gap-y-12"
        >
          {PLATFORMS.map(({ key, label, href, render }) => (
            <li key={key} className="shrink-0">
              <a
                href={href}
                target="_blank"
                rel="noopener noreferrer"
                data-testid={`${testId}-platform-${key}`}
                aria-label={`Download YCN on ${label}`}
                title={label}
                className="inline-flex items-center justify-center h-24 md:h-28 min-w-[130px] md:min-w-[150px] px-2 opacity-95 hover:opacity-100 hover:scale-105 transition-all duration-300"
              >
                {render()}
              </a>
            </li>
          ))}
        </ul>

        <p className="mt-14 text-center text-[11px] tracking-[0.3em] uppercase text-white/40">
          More Platforms Coming Soon
        </p>
      </div>
    </section>
  );
}
